import z from "zod";

export const SearchSortSchema = z.enum([
  "recommended",
  "price-asc",
  "price-desc",
  "number-of-ratings",
]);
const NumberOfGuestsValues = [
  "",
  "any",
  "gte-10",
  "gte-25",
  "gte-50",
  "gte-100",
  "gte-200",
  "gte-500",
  "gte-1000",
] as const;

/** Map legacy "bis X" / "mehr als 1000" URL values to the new "mind. X" keys. */
function normalizeLegacyGuestsParam(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (value === "mt-1000") return "gte-1000";
  const lteMatch = /^lte-(\d+)$/.exec(value);
  if (lteMatch) return `gte-${lteMatch[1]}`;
  return value;
}

export const NumberOfGuestsSchema = z.preprocess(
  normalizeLegacyGuestsParam,
  z.enum(NumberOfGuestsValues),
);

export const SearchFilterSchema = z.object({
  occasion: z.number().array().default([]),
  locationType: z.number().array().default([]),
  seating: z.string().default(""),
  locationSettings: z
    .object({
      indoor: z.boolean().default(false),
      outdoor: z.boolean().default(false),
      mixed: z.boolean().default(false),
    })
    .default({
      indoor: false,
      outdoor: false,
      mixed: false,
    }),
  locationSize: z
    .object({
      maxArea: z.number().default(0),
      indoorAreaTotal: z.number().default(0),
      maxHeight: z.number().default(0),
    })
    .default({
      maxArea: 0,
      indoorAreaTotal: 0,
      maxHeight: 0,
    }),
  locationOpening: z.number().default(0),
  locationRenovation: z.number().default(0),
  commonProperties: z
    .object({
      accessible: z.boolean().default(false),
      dimmable: z.boolean().default(false),
      daylight: z.boolean().default(false),
    })
    .default({
      accessible: false,
      dimmable: false,
      daylight: false,
    }),
  numberOfNeededRooms: z.number().optional(),
  outerAreaSize: z.number().default(0),
  stage: z.boolean().default(false),
  numberOfParkingSpaces: z.number().default(0),
  parkingLots: z.enum(["yes", "noOwn"]).optional(),
  hasAccommodations: z.boolean().optional(),
  hotelRooms: z.number().default(0),
  hotelStars: z.number().default(0),
  hotelTypes: z.number().array().default([]),
  budget: z
    .object({
      type: z
        .enum(["rateDaily", "rateHourly", "minBudgetTotal", "minBudgetPerPerson"])
        .default("rateDaily"),
      currency: z.enum(["EUR", "CHF", "USD"]).default("EUR"),
      from: z.number().nullable().default(null),
      to: z.number().nullable().default(null),
    })
    .optional(),
  cateringBinding: z.enum(["", "none", "fixed"]).default(""),
  techBinding: z.enum(["", "none", "fixed"]).default(""),
  infrastructure: z.number().array().default([]),
  distances: z
    .array(
      z.object({
        type: z.enum([
          "motorway",
          "railstation",
          "airport",
          "city-center",
          "public-transport",
        ]),
        maxDistance: z.number(),
      })
    )
    .default([]),
  rentalDuration: z
    .object({
      days: z
        .enum([
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ])
        .array()
        .default([]),
      from: z.number().nullable().default(null),
      to: z.number().nullable().default(null),
    })
    .default({
      days: [],
      from: null,
      to: null,
    }),
  deliveryLogistics: z
    .object({
      truckAccess: z.boolean().default(false),
      carAccess: z.boolean().default(false),
      freightElevator: z.boolean().default(false),
      loadingRamp: z.boolean().default(false),
    })
    .default({
      truckAccess: false,
      carAccess: false,
      freightElevator: false,
      loadingRamp: false,
    }),
  leisureSportWellness: z
    .object({
      leisure: z
        .object({
          sightseeing: z.boolean().default(false),
        })
        .default({
          sightseeing: false,
        }),
      sport: z
        .object({
          golf: z.boolean().default(false),
          tennis: z.boolean().default(false),
          sailing: z.boolean().default(false),
          ski: z.boolean().default(false),
        })
        .default({
          golf: false,
          tennis: false,
          sailing: false,
          ski: false,
        }),
      wellness: z
        .object({
          sauna: z.boolean().default(false),
          massage: z.boolean().default(false),
          indoorPool: z.boolean().default(false),
          outdoorPool: z.boolean().default(false),
        })
        .default({
          sauna: false,
          massage: false,
          indoorPool: false,
          outdoorPool: false,
        }),
    })
    .default({
      leisure: {
        sightseeing: false,
      },
      sport: {
        golf: false,
        tennis: false,
        sailing: false,
        ski: false,
      },
      wellness: {
        sauna: false,
        massage: false,
        indoorPool: false,
        outdoorPool: false,
      },
    }),
  certifications: z
    .object({
      gstcHotel: z.boolean().default(false),
      greenKey: z.boolean().default(false),
      iso14001: z.boolean().default(false),
    })
    .default({
      gstcHotel: false,
      greenKey: false,
      iso14001: false,
    }),
  equipment: z
    .object({
      acPower: z
        .object({
          volt110: z.boolean().default(false),
          volt230: z.boolean().default(false),
        })
        .default({
          volt110: false,
          volt230: false,
        }),
      threePhasePower: z
        .object({
          amp16: z.boolean().default(false),
          amp32: z.boolean().default(false),
          amp64: z.boolean().default(false),
          amp128: z.boolean().default(false),
        })
        .default({
          amp16: false,
          amp32: false,
          amp64: false,
          amp128: false,
        }),
      technology: z
        .object({
          lighting: z.boolean().default(false),
          presentationVideo: z.boolean().default(false),
          audio: z.boolean().default(false),
          heatingClimate: z.boolean().default(false),
        })
        .default({
          lighting: false,
          presentationVideo: false,
          audio: false,
          heatingClimate: false,
        }),
      specialTechnology: z
        .object({
          rigging: z.boolean().default(false),
          v1: z.boolean().default(false),
          v2: z.boolean().default(false),
          v3: z.boolean().default(false),
          v4: z.boolean().default(false),
          v5: z.boolean().default(false),
        })
        .default({
          rigging: false,
          v1: false,
          v2: false,
          v3: false,
          v4: false,
          v5: false,
        }),
      internet: z
        .object({
          g5: z.boolean().default(false),
          g4: z.boolean().default(false),
          g3: z.boolean().default(false),
          g2: z.boolean().default(false),
          minBandwidth: z.number().default(0),
        })
        .default({
          g5: false,
          g4: false,
          g3: false,
          g2: false,
          minBandwidth: 0,
        }),
      furniture: z.enum(["available", "bookable", "none"]).default("none"),
      sanitary: z
        .object({
          wcWomen: z.boolean().default(false),
          wcMen: z.boolean().default(false),
          wcAccessible: z.boolean().default(false),
        })
        .default({
          wcWomen: false,
          wcMen: false,
          wcAccessible: false,
        }),
      other: z
        .object({
          smokingArea: z.boolean().default(false),
          artistDressingRooms: z.boolean().default(false),
          noNoiseRestrictions: z.boolean().default(false),
          shower: z.boolean().default(false),
          backstageArea: z.boolean().default(false),
          danceFloor: z.boolean().default(false),
        })
        .default({
          smokingArea: false,
          artistDressingRooms: false,
          noNoiseRestrictions: false,
          shower: false,
          backstageArea: false,
          danceFloor: false,
        }),
    })
    .default({
      acPower: {
        volt110: false,
        volt230: false,
      },
      threePhasePower: {
        amp16: false,
        amp32: false,
        amp64: false,
        amp128: false,
      },
      technology: {
        lighting: false,
        presentationVideo: false,
        audio: false,
        heatingClimate: false,
      },
      specialTechnology: {
        rigging: false,
        v1: false,
        v2: false,
        v3: false,
        v4: false,
        v5: false,
      },
      internet: {
        g5: false,
        g4: false,
        g3: false,
        g2: false,
        minBandwidth: 0,
      },
      furniture: "none",
      sanitary: {
        wcWomen: false,
        wcMen: false,
        wcAccessible: false,
      },
      other: {
        smokingArea: false,
        artistDressingRooms: false,
        noNoiseRestrictions: false,
        shower: false,
        backstageArea: false,
        danceFloor: false,
      },
    }),
});

export function countSetFilters(
  filters: z.infer<typeof SearchFilterSchema>,
  type: number | null,
  guests: NumberOfGuests,
  locationIdOrName?: string,
  location?: string[]
): number {
  let count = 0;

  // Standortsuche: Loc. ID oder Name
  if ((locationIdOrName?.trim() ?? "") !== "") count++;

  // Standortsuche: Location Array (Orte)
  if (location && location.length > 0) count++;

  // Arrays - count if they have items
  if (
    (filters.occasion.length ?? 0) > 0 ||
    type ||
    (guests !== "" && guests !== "any")
  )
    count++;
  if (filters.locationType.length > 0) count++;

  // String - count if non-empty
  if (filters.seating.trim() !== "") count++;

  // Location settings - count each true boolean
  if (filters.locationSettings.indoor) count++;
  if (filters.locationSettings.outdoor) count++;
  if (filters.locationSettings.mixed) count++;

  // Location size - count each non-zero number
  if (filters.locationSize.maxArea > 0) count++;
  if (filters.locationSize.indoorAreaTotal > 0) count++;
  if (filters.locationSize.maxHeight > 0) count++;

  // Numbers - count if greater than 0
  if (filters.locationOpening > 0) count++;
  if (filters.locationRenovation > 0) count++;

  // Common properties - count each true boolean
  if (filters.commonProperties.accessible) count++;
  if (filters.commonProperties.dimmable) count++;
  if (filters.commonProperties.daylight) count++;

  if ((filters.numberOfNeededRooms ?? 0) > 0) count++;
  if (filters.outerAreaSize > 0) count++;
  if (filters.stage) count++;
  if (filters.numberOfParkingSpaces > 0) count++;
  if (filters.parkingLots !== undefined) count++;
  if (filters.hasAccommodations === true) count++;
  if (filters.hotelRooms > 0) count++;
  if (filters.hotelStars > 0) count++;
  if (filters.hotelTypes.length > 0) count++;
  if (
    filters.budget &&
    (filters.budget.from !== null || filters.budget.to !== null)
  )
    count++;

  if (filters.cateringBinding !== "") count++;
  if (filters.techBinding !== "") count++;

  if (filters.infrastructure.length > 0) count++;
  if (filters.distances.length > 0) count++;
  if (filters.rentalDuration.days.length > 0) count++;
  if (filters.rentalDuration.from !== null) count++;
  if (filters.rentalDuration.to !== null) count++;

  // Delivery logistics - count each true boolean
  if (filters.deliveryLogistics?.truckAccess) count++;
  if (filters.deliveryLogistics?.carAccess) count++;
  if (filters.deliveryLogistics?.freightElevator) count++;
  if (filters.deliveryLogistics?.loadingRamp) count++;

  // Leisure / Sport / Wellness - count each true boolean
  if (filters.leisureSportWellness?.leisure?.sightseeing) count++;
  if (filters.leisureSportWellness?.sport?.golf) count++;
  if (filters.leisureSportWellness?.sport?.tennis) count++;
  if (filters.leisureSportWellness?.sport?.sailing) count++;
  if (filters.leisureSportWellness?.sport?.ski) count++;
  if (filters.leisureSportWellness?.wellness?.sauna) count++;
  if (filters.leisureSportWellness?.wellness?.massage) count++;
  if (filters.leisureSportWellness?.wellness?.indoorPool) count++;
  if (filters.leisureSportWellness?.wellness?.outdoorPool) count++;

  // Certifications - count each true boolean
  if (filters.certifications?.gstcHotel) count++;
  if (filters.certifications?.greenKey) count++;
  if (filters.certifications?.iso14001) count++;

  // Equipment - count each true boolean and non-default values
  if (filters.equipment?.acPower?.volt110) count++;
  if (filters.equipment?.acPower?.volt230) count++;
  if (filters.equipment?.threePhasePower?.amp16) count++;
  if (filters.equipment?.threePhasePower?.amp32) count++;
  if (filters.equipment?.threePhasePower?.amp64) count++;
  if (filters.equipment?.threePhasePower?.amp128) count++;
  if (filters.equipment?.technology?.lighting) count++;
  if (filters.equipment?.technology?.presentationVideo) count++;
  if (filters.equipment?.technology?.audio) count++;
  if (filters.equipment?.technology?.heatingClimate) count++;
  if (filters.equipment?.specialTechnology?.rigging) count++;
  if (filters.equipment?.specialTechnology?.v1) count++;
  if (filters.equipment?.specialTechnology?.v2) count++;
  if (filters.equipment?.specialTechnology?.v3) count++;
  if (filters.equipment?.specialTechnology?.v4) count++;
  if (filters.equipment?.specialTechnology?.v5) count++;
  if (filters.equipment?.internet?.g5) count++;
  if (filters.equipment?.internet?.g4) count++;
  if (filters.equipment?.internet?.g3) count++;
  if (filters.equipment?.internet?.g2) count++;
  if (filters.equipment?.internet?.minBandwidth > 0) count++;
  if (filters.equipment?.furniture !== "none") count++;
  if (filters.equipment?.sanitary?.wcWomen) count++;
  if (filters.equipment?.sanitary?.wcMen) count++;
  if (filters.equipment?.sanitary?.wcAccessible) count++;
  if (filters.equipment?.other?.smokingArea) count++;
  if (filters.equipment?.other?.artistDressingRooms) count++;
  if (filters.equipment?.other?.noNoiseRestrictions) count++;
  if (filters.equipment?.other?.shower) count++;
  if (filters.equipment?.other?.backstageArea) count++;
  if (filters.equipment?.other?.danceFloor) count++;

  return count;
}

export const SearchParamsSchema = z.object({
  location: z
    .array(z.string().trim().min(1).max(120))
    .max(8)
    .refine(
      (values) => new Set(values.map((value) => value.toLowerCase())).size === values.length,
      "Location values must be unique",
    )
    .default([]),
  /** Optional Place IDs aligned with `location[]` (same indices). */
  placeIds: z.array(z.string().trim().max(512)).max(8).optional(),
  guests: NumberOfGuestsSchema.default(""),
  type: z.number().nullable().default(null),
  filters: SearchFilterSchema.optional(),
  sort: SearchSortSchema.default("recommended"),
  rating: z.number().default(0),
  searchRadius: z.number().optional(),
  /** Location ID (exact match) or part of location name (partial match). */
  locationIdOrName: z.string().trim().max(120).optional(),
  /**
   * CMS-friendly prefill params. When present, the search page rewrites to the canonical
   * filters URL. Use in links: /search?occasion=10&loc=Berlin&locationType=3
   */
  occasion: z.union([z.string(), z.number()]).optional().transform((v) => (v === undefined ? undefined : String(v))),
  loc: z.string().max(960).optional(),
  /** CMS-friendly comma-separated Place IDs aligned with `loc`. */
  locPlaceIds: z.string().max(4096).optional(),
  locationType: z.union([z.string(), z.number()]).optional().transform((v) => (v === undefined ? undefined : String(v))),
}).superRefine((value, ctx) => {
  if (value.placeIds && value.placeIds.length !== value.location.length) {
    ctx.addIssue({
      code: "custom",
      path: ["placeIds"],
      message: "placeIds must be aligned with location",
    });
  }
});

export type SearchFilter = z.infer<typeof SearchFilterSchema>;
export type SearchParams = z.infer<typeof SearchParamsSchema>;
export type SearchSort = z.infer<typeof SearchSortSchema>;
export type NumberOfGuests = z.infer<typeof NumberOfGuestsSchema>;
